import './Quote.css';

import { useApolloClient } from '@apollo/client';
import { WorkflowVersion } from '@foxden/version-controller-client';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { z } from 'zod';

import { useAuth } from '../../auth/AuthProvider';
import { PaymentClient } from '../../backend-client/paymentBackend';
import { PaymentGraphqlClient } from '../../backend-client/paymentGraphqlBackend';
import { RatingQuotingClient } from '../../backend-client/ratingQuotingBackend';
import Loading from '../../components/Loading/Loading';
import {
  BusinessInfo,
  IsApplicationGroupFinalizedDocument,
  IsApplicationGroupFinalizedQuery,
  IsApplicationGroupFinalizedQueryVariables
} from '../../generated/graphql';
import { UserPaymentPlanOptionByQuoteId } from '../../generated/payment-graphql';
import {
  AdditionalInsured as AdditionalInsuredInfo,
  GetQuoteOk,
  UpdateCoverageInput,
  UpdateCoverageOutput
} from '../../generated/rating-quoting-graphql';
import { markApplicationEditStarted } from '../../utils/applicationEditSession';
import {
  APPLICATION_FINALIZED_CHECK_FAILED_MESSAGE,
  APPLICATION_FINALIZED_FALLBACK_MESSAGE,
  getApplicationFinalizedMessage
} from '../../utils/isApplicationFinalizedError';
import { Context, ContextValue } from '../../utils/store';
import { useBlockBrowserBack } from '../../utils/useBlockBrowserBack';
import ErrorPage from '../Error/Error';
import AdditionalInsured from './components/AdditionalInsured';
import ApplicantInfo from './components/ApplicantInfo';
import BackNavigationPopup from './components/BackNavigationPopup';
import BuyNowButton from './components/BuyNowButton';
import Coverages from './components/Coverages';
import { AvailableCoverage } from './components/Coverages/AvailableCoverage';
import { IneligibleCoverage } from './components/Coverages/IneligibleCoverage';
import CustomerAgreement from './components/CustomerAgreement';
import Premium from './components/Premium';
import { SummaryContainer } from './components/Summary';

export interface CoveragesDataInput {
  name: string;
  chooseCoverage: boolean;
  finalPremium: number;
  limits: Record<string, number>;
  /** E&O only: claims-made retroactive date, YYYY-MM-DD. */
  retroactiveDate?: string | null;
}

type ApplicationQuoteGroup = {
  lobType: string;
  applicationId: string;
  quoteData: GetQuoteOk;
};

// A product whose getQuote was declined. It has no quote data, so we only need
// its identifiers to render the ineligible card.
type DeclinedQuoteGroup = {
  lobType: string;
  applicationId: string;
};

interface QuoteProps {
  applicationGroupId: string;
  applicationQuoteGroups: [ApplicationQuoteGroup, ...ApplicationQuoteGroup[]]; // guarantee at least one item in array
  /** Products the backend declined while at least one product is quotable. */
  declinedQuoteGroups?: DeclinedQuoteGroup[];
  /** LOBs offered/created but not active — rendered as selectable "add" cards. */
  addableLobs?: string[];
  /** Add a LOB to the quote (NCP-4545). */
  onAddLob?: (lobType: string) => void;
  /** Remove a LOB from the quote (NCP-4545). */
  onDeselectLob?: (lobType: string) => void;
  /** Whether a LOB may be removed — false when only one LOB is active. */
  canDeselectLob?: boolean;
  versionData: WorkflowVersion;
  shouldShowCustomerAgreement?: boolean;
  isTnCLinkSent?: boolean;
  businessInfo: BusinessInfo;
  refetchBusinessInfo?: () => void;
}

export type NewQuoteForApp = {
  applicationId: string;
  quoteId: string;
  lobType: string;
};

const additionalInsuedInfoSchema = z.object({
  name: z.string().min(1),
  address: z.object({
    street: z.string().min(1),
    postalCode: z.string().min(5),
    province: z.string().min(2),
    city: z.string().min(1),
    address2: z.string().nullable().optional()
  }),
  email: z
    .string()
    .refine((value) => value === '')
    .or(z.email())
});

// This component handles rendering of active quotes (quoteData is always defined)
// Expired quote handling is done in index.tsx

const Quote: React.FC<QuoteProps> = (props) => {
  const context: ContextValue | null = useContext(Context);

  const {
    applicationGroupId,
    applicationQuoteGroups,
    declinedQuoteGroups,
    addableLobs,
    onAddLob,
    onDeselectLob,
    canDeselectLob,
    versionData,
    shouldShowCustomerAgreement,
    isTnCLinkSent: isTnCLinkSentProp,
    businessInfo,
    refetchBusinessInfo
  } = props;

  const primaryApplicationId = applicationQuoteGroups[0].applicationId;

  const { isAuthenticated, isLoading: isAuthProviderLoading } = useAuth();

  const history = useHistory();

  // NCP-5230: the quote on this page is already generated, so a browser Back
  // press must not walk the customer back into the survey it was priced from.
  // Absorb the press and explain that "Edit" is the way back in.
  const [isBackBlockedNoticeOpen, setIsBackBlockedNoticeOpen] = useState(false);
  useBlockBrowserBack(() => setIsBackBlockedNoticeOpen(true));

  const isCustomerAgreementActive =
    shouldShowCustomerAgreement ??
    (context ? context.isCustomerAgreementActive : false);

  const [quoteGroups, setQuoteGroups] = useState<
    [ApplicationQuoteGroup, ...ApplicationQuoteGroup[]]
  >(applicationQuoteGroups);

  const applyNewQuoteIds = (newQuoteIds: NewQuoteForApp[]) => {
    setQuoteGroups(
      (groups) =>
        groups.map((g) => {
          const match = newQuoteIds.find(
            (n) =>
              n.applicationId === g.applicationId && n.lobType === g.lobType
          );
          return match
            ? { ...g, quoteData: { ...g.quoteData, quoteId: match.quoteId } }
            : g;
        }) as [ApplicationQuoteGroup, ...ApplicationQuoteGroup[]]
    );
  };

  // TODO: for now, use primaryQuoteResponse to drive the quote page since we only support one product. In the future, if we support multiple products endorsement, we might need to change the logic to make sure the response of each product update is correctly reflected on the page.
  const primaryQuoteResponse = quoteGroups.find(
    (g) => g.applicationId === primaryApplicationId
  )?.quoteData as GetQuoteOk;

  const [country, setCountry] = useState('');
  const [province, setProvince] = useState('');

  // Total annual premium summed across every product (lobType) in the group.
  // Derived from quoteGroups, which is kept up to date on each coverage
  // update/reset, so it stays in sync without a separate premium state.
  const premium = quoteGroups.reduce(
    (sum, group) => sum + group.quoteData.premium,
    0
  );

  // Per-application: each quote group has its own additional-insured list / fill
  // flag so a multi-LOB group (e.g. GL + EO) can carry independent additional
  // insured info for each product.
  const [
    additionalInsuredInfoListByAppId,
    setAdditionalInsuredInfoListByAppId
  ] = useState<Record<string, AdditionalInsuredInfo[] | undefined>>({});
  const [
    fillAllAdditionalInsuredInfoByAppId,
    setFillAllAdditionalInsuredInfoByAppId
  ] = useState<Record<string, boolean | undefined>>({});

  const makeSetAdditionalInsuredInfoListFor = (
    appId: string
  ): React.Dispatch<
    React.SetStateAction<AdditionalInsuredInfo[] | undefined>
  > => (action) => {
    setAdditionalInsuredInfoListByAppId((prev) => {
      const current = prev[appId];
      const next =
        typeof action === 'function'
          ? (action as (
              prev: AdditionalInsuredInfo[] | undefined
            ) => AdditionalInsuredInfo[] | undefined)(current)
          : action;
      return { ...prev, [appId]: next };
    });
  };

  const makeSetFillAllAdditionalInsuredInfoFor = (
    appId: string
  ): React.Dispatch<React.SetStateAction<boolean | undefined>> => (action) => {
    setFillAllAdditionalInsuredInfoByAppId((prev) => {
      const current = prev[appId];
      const next =
        typeof action === 'function'
          ? (action as (prev: boolean | undefined) => boolean | undefined)(
              current
            )
          : action;
      return { ...prev, [appId]: next };
    });
  };

  const isEndorsement = primaryQuoteResponse.transactionType === 'Endorsement';
  // NCP-5396: the submitted marker only covers New Business, so that is the
  // only journey where returning to underwriting is offered.
  const isNewBusiness = primaryQuoteResponse.transactionType === 'New Business';

  const [updateCoverageData, setUpdateCoverageData] = useState<{
    data: UpdateCoverageOutput;
    applicationId: string;
  }>();
  const [
    isUpdateCoverageByRatingQuotingLoading,
    setIsUpdateCoverageByRatingQuotingLoading
  ] = useState<boolean>(false);
  const [updateCoverageError, setUpdateCoverageError] = useState<unknown>();

  const [
    applicationFinalizedMessage,
    setApplicationFinalizedMessage
  ] = useState<string | null>(null);

  // "Edit" navigates instead of mutating, so no mutation guard can reject it.
  // The pre-check below is the equivalent server-side validation, and it holds
  // the page in Loading while it runs.
  const [
    isCheckingApplicationFinalized,
    setIsCheckingApplicationFinalized
  ] = useState<boolean>(false);
  const apolloClient = useApolloClient();

  const [quoteId, setQuoteId] = useState<string | undefined>(undefined); // this quoteId is getting from getQuoteOK response

  // Payment plan options keyed by lobType (e.g. "GL", "EO"); each value is the
  // array of options for that product in the application group.
  const [paymentOptionData, setPaymentOptionData] = useState<
    Record<string, Array<UserPaymentPlanOptionByQuoteId>>
  >();
  const [
    isGetUserPaymentPlanOptionByGroupIdLoading,
    setIsGetUserPaymentPlanOptionByGroupIdLoading
  ] = useState<boolean>(false);
  // True once the payment-plan fetch below has settled at least once. Only that
  // first fetch is allowed to raise the loading flag, because the flag gates a
  // full-page <Loading /> that unmounts this whole subtree — see the effect.
  const hasFetchedPaymentOptions = useRef(false);
  // True while a background refresh of the payment options is in flight. The
  // summary total and the monthly premium are derived from that data, so they
  // show a spinner instead of a figure that belongs to the previous quote.
  const [
    isPaymentOptionRefreshing,
    setIsPaymentOptionRefreshing
  ] = useState<boolean>(false);
  const [
    getUserPaymentPlanOptionByGroupIdError,
    setGetUserPaymentPlanOptionByGroupIdError
  ] = useState<unknown>();
  const [
    paymentGraphqlClient,
    setPaymentGraphqlClient
  ] = useState<PaymentGraphqlClient | null>(null);

  useEffect(() => {
    if (quoteId) {
      const client = new PaymentGraphqlClient(
        versionData.paymentBackendVersion,
        quoteId
      );
      setPaymentGraphqlClient(client);
    }
  }, [quoteId, versionData]);

  useEffect(() => window.scrollTo(0, 0), []);

  useEffect(() => {
    if (context && businessInfo.companyName) {
      const { setHeaderTitle } = context;
      setHeaderTitle(businessInfo.companyName);
    }
  }, [businessInfo.companyName, context]);

  useEffect(() => {
    // assign quoteId to state
    const { region, quoteId: id } = primaryQuoteResponse;
    setQuoteId(id);

    setCountry(region == 'Canada' ? 'Canada' : 'United States of America');
    // for now, consider "Canada" as province for data record, other US states will use individual state name
    setProvince(region);
  }, [primaryQuoteResponse, country, province]);

  useEffect(() => {
    // For each group whose additionalInsured coverage is chosen, seed/reset its
    // list to the backend-provided value (or a single empty row).
    setAdditionalInsuredInfoListByAppId((prev) => {
      let next = prev;
      quoteGroups.forEach((g) => {
        const aiCoverage = g.quoteData.coveragesData.find(
          (c) => c.name === 'additionalInsured'
        );
        if (aiCoverage?.chooseCoverage) {
          const defaultList = g.quoteData.additionalInsuredInfoList || [
            { name: '', address: undefined, email: '' }
          ];
          if (next === prev) next = { ...prev };
          next[g.applicationId] = defaultList;
        }
      });
      return next;
    });
  }, [quoteGroups]);

  useEffect(() => {
    if (updateCoverageData) {
      const { data, applicationId } = updateCoverageData;

      setQuoteGroups(
        (groups) =>
          groups.map((g) =>
            g.applicationId === applicationId
              ? {
                  ...g,
                  quoteData: {
                    ...g.quoteData,
                    coveragesData: data.coveragesData,
                    quoteId: data.quoteId,
                    premium: data.finalPremium
                  }
                }
              : g
          ) as [ApplicationQuoteGroup, ...ApplicationQuoteGroup[]]
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateCoverageData]);

  // `quoteGroups` is a dependency because a re-quote can change what the
  // customer owes, so the payment plan has to be re-read. That makes every run
  // after the first a BACKGROUND REFRESH: the page is already on screen with
  // data the customer is looking at.
  //
  // NCP-5343: raising the loading flag on those refreshes was unmounting the
  // page. The flag gates a full-page <Loading /> (see below), so a successful
  // applicant-info save — which calls applyNewQuoteIds and therefore replaces
  // `quoteGroups` — blanked the quote page and remounted it. Every child lost
  // its state on the way back; the ApplicantInfo panel snapped shut, hiding the
  // values that had just been saved, and the customer got no confirmation at
  // all. So only the first fetch gates: a refresh swaps the data in underneath
  // a page that stays mounted.
  useEffect(() => {
    if (!isEndorsement && applicationGroupId !== undefined) {
      if (!hasFetchedPaymentOptions.current) {
        setIsGetUserPaymentPlanOptionByGroupIdLoading(true);
      } else {
        // The page stays up; only the payment-derived figures are stale until
        // this resolves, so they alone are marked as refreshing.
        setIsPaymentOptionRefreshing(true);
      }

      const paymentClient = new PaymentClient(
        versionData.paymentBackendVersion
      );

      paymentClient
        .getUserPaymentPlanOptionsByGroupId(applicationGroupId)
        .then((data) => {
          hasFetchedPaymentOptions.current = true;
          setPaymentOptionData(data);
          setIsGetUserPaymentPlanOptionByGroupIdLoading(false);
          setIsPaymentOptionRefreshing(false);
        })
        .catch((err) => {
          hasFetchedPaymentOptions.current = true;
          setGetUserPaymentPlanOptionByGroupIdError(err);
          setIsGetUserPaymentPlanOptionByGroupIdLoading(false);
          setIsPaymentOptionRefreshing(false);
        });
    }
  }, [applicationGroupId, versionData, isEndorsement, quoteGroups]);

  const onUpdateCoverage = async (input: UpdateCoverageInput) => {
    // call backend to update coverage and get a new premium
    setIsUpdateCoverageByRatingQuotingLoading(true);

    const ratingQuotingClient = new RatingQuotingClient(
      versionData.quoteBackendVersion
    );
    ratingQuotingClient
      .updateCoverage(input)
      .then((data) => {
        setUpdateCoverageData({ data, applicationId: input.applicationId });
        setIsUpdateCoverageByRatingQuotingLoading(false);
      })
      .catch((err) => {
        const finalizedMessage = getApplicationFinalizedMessage(err);
        if (finalizedMessage) {
          setApplicationFinalizedMessage(finalizedMessage);
        } else {
          setUpdateCoverageError(err);
        }
        setIsUpdateCoverageByRatingQuotingLoading(false);
      });
  };

  // "Edit" on a policy card sends the applicant back to the business info page
  // for this application group, where the answers that drive the quote live.
  // Returning to the application is only allowed while the group is still
  // editable — the same signed/paid rule the quote mutations enforce via
  // assertApplicationNotFinalized — so validate server-side first. Fails
  // closed: if the check itself cannot complete, the navigation is blocked.
  const onEdit = async () => {
    setApplicationFinalizedMessage(null);
    setIsCheckingApplicationFinalized(true);

    try {
      const { data } = await apolloClient.query<
        IsApplicationGroupFinalizedQuery,
        IsApplicationGroupFinalizedQueryVariables
      >({
        query: IsApplicationGroupFinalizedDocument,
        variables: { applicationGroupId },
        // Must hit the network every time: the whole point is to catch a group
        // that was signed or paid after this page loaded (e.g. in another tab),
        // which is exactly the state a cached result would hide.
        fetchPolicy: 'network-only'
      });
      const finalizedState = data?.isApplicationGroupFinalized;

      if (!finalizedState) {
        setApplicationFinalizedMessage(
          APPLICATION_FINALIZED_CHECK_FAILED_MESSAGE
        );
        return;
      }

      if (finalizedState.isFinalized) {
        setApplicationFinalizedMessage(
          finalizedState.message ?? APPLICATION_FINALIZED_FALLBACK_MESSAGE
        );
        return;
      }

      // NCP-5516: the underwriting routes cannot tell an edit from a first
      // application, so record it here, where we know.
      markApplicationEditStarted();
      history.push(`/${applicationGroupId}`);
    } catch {
      setApplicationFinalizedMessage(
        APPLICATION_FINALIZED_CHECK_FAILED_MESSAGE
      );
    } finally {
      setIsCheckingApplicationFinalized(false);
    }
  };

  if (updateCoverageError && !applicationFinalizedMessage) {
    return <ErrorPage />;
  }

  if (
    isUpdateCoverageByRatingQuotingLoading ||
    isCheckingApplicationFinalized ||
    isGetUserPaymentPlanOptionByGroupIdLoading ||
    !quoteId ||
    isAuthProviderLoading
  ) {
    return <Loading />;
  }

  if (getUserPaymentPlanOptionByGroupIdError) {
    throw Error('Backend error');
  }

  // Total payable is the sum of every product's (lobType) total payable. Within
  // a single lobType the totalPayable is identical across payment plan options,
  // so we read it from the first option of each product.
  const totalPayable = paymentOptionData
    ? Object.values(paymentOptionData).reduce(
        (sum, optionsForLob) => sum + (optionsForLob[0]?.totalPayable ?? 0),
        0
      )
    : premium;

  const showSendQuoteButton = isAuthenticated;

  const validateAdditionalInsured = () => {
    let validataionResult = true;
    quoteGroups.forEach((g) => {
      const aiCoverage = g.quoteData.coveragesData.find(
        (c) => c.name === 'additionalInsured'
      );
      const list = additionalInsuredInfoListByAppId[g.applicationId];
      if (!aiCoverage?.chooseCoverage || !list) return;
      list.forEach((info) => {
        try {
          additionalInsuedInfoSchema.parse(info);
        } catch (e) {
          setFillAllAdditionalInsuredInfoByAppId((prev) => ({
            ...prev,
            [g.applicationId]: false
          }));
          validataionResult = false;
          return;
        }
      });
    });
    return validataionResult;
  };

  if (premium <= 0 && !isEndorsement) {
    return (
      <Redirect
        to={{
          pathname: '/complete',
          state: {
            country,
            province
          }
        }}
      />
    );
  }

  return (
    <div className="relative page-container font-raleway lg:my-4">
      {applicationFinalizedMessage && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center justify-between"
          role="alert"
        >
          <div>
            <p className="font-bold">Unable to modify quote</p>
            <p>{applicationFinalizedMessage}</p>
          </div>
          <button
            className="ml-4 shrink-0 underline font-bold text-red-700"
            onClick={() => window.location.reload()}
          >
            Refresh
          </button>
        </div>
      )}
      <div className="mt-1 quote-box">
        <div className="quote-header md:hidden text-center mb-2">
          <p className="text-2xl text-navy-dark font-bold">
            {isCustomerAgreementActive
              ? 'Client Agreements'
              : 'Tailored Quote For'}
            :
          </p>
          <p className="text-xl font-medium">{businessInfo.companyName}</p>
        </div>
        <Premium
          yearlyPremium={premium}
          isEndorsement={isEndorsement}
          paymentOptionData={paymentOptionData}
          isPaymentOptionRefreshing={isPaymentOptionRefreshing}
        />
        <SummaryContainer
          quoteGroups={quoteGroups}
          totalPayable={totalPayable}
          isEndorsement={isEndorsement}
          isPaymentOptionRefreshing={isPaymentOptionRefreshing}
        />
        {isCustomerAgreementActive && quoteId ? (
          <CustomerAgreement
            quoteId={quoteId}
            applicationGroupId={applicationGroupId}
            versionData={versionData}
            customerName={businessInfo.customerName || ''}
            email={businessInfo.email || ''}
            paymentGraphqlClient={paymentGraphqlClient}
            isTnCLinkSent={isTnCLinkSentProp ?? false}
            isEndorsement={isEndorsement}
            coverageStartDate={businessInfo?.coverageStartDate}
          />
        ) : (
          <div className="coverages-list">
            <ApplicantInfo
              businessInfo={businessInfo}
              applicationGroupId={applicationGroupId}
              country={country}
              province={province}
              applyNewQuoteIds={applyNewQuoteIds}
              additionalInsuredInfoListByAppId={
                additionalInsuredInfoListByAppId
              }
              refetchBusinessInfo={refetchBusinessInfo}
            />
            {quoteGroups.map((g) => {
              const aiCoverage = g.quoteData.coveragesData.find(
                (c) => c.name === 'additionalInsured'
              );
              return (
                <Coverages
                  key={g.quoteData.quoteId}
                  coverageInput={g.quoteData}
                  onUpdateCoverage={onUpdateCoverage}
                  onEdit={onEdit}
                  isNewBusiness={isNewBusiness}
                  applicationId={g.applicationId}
                  lobType={g.lobType}
                  onDeselectLob={isEndorsement ? undefined : onDeselectLob}
                  canDeselectLob={canDeselectLob}
                  // Additional insured lives inside the policy card that owns
                  // the coverage (GL) rather than in a section of its own.
                  additionalInsured={
                    aiCoverage ? (
                      <AdditionalInsured
                        coverageData={aiCoverage}
                        onUpdateCoverage={onUpdateCoverage}
                        region={g.quoteData.region}
                        quoteId={g.quoteData.quoteId}
                        applicationId={g.applicationId}
                        additionalInsuredInfoList={
                          additionalInsuredInfoListByAppId[g.applicationId]
                        }
                        setAdditionalInsuredInfoList={makeSetAdditionalInsuredInfoListFor(
                          g.applicationId
                        )}
                        fillAllAdditionalInsuredInfo={
                          fillAllAdditionalInsuredInfoByAppId[g.applicationId]
                        }
                        setFillAllAdditionalInsuredInfo={makeSetFillAllAdditionalInsuredInfoFor(
                          g.applicationId
                        )}
                        disabled={g.quoteData.disableAdditionalInsuredCheckbox}
                      />
                    ) : null
                  }
                />
              );
            })}
            {declinedQuoteGroups?.map((g) => (
              <IneligibleCoverage key={g.applicationId} lobType={g.lobType} />
            ))}
            {addableLobs?.map((lobType) => (
              <AvailableCoverage
                key={`available-${lobType}`}
                lobType={lobType}
                onAdd={onAddLob}
                region={quoteGroups[0].quoteData.region}
                carrierPartner={quoteGroups[0].quoteData.carrierPartner}
              />
            ))}
          </div>
        )}
        <BuyNowButton
          quoteId={quoteId}
          quotesForDownload={quoteGroups.map((g) => ({
            quoteId: g.quoteData.quoteId,
            quoteNumber: g.quoteData.quoteNumber,
            lobType: g.lobType
          }))}
          primaryApplicationId={primaryApplicationId}
          applicationGroupId={applicationGroupId}
          applyNewQuoteIds={applyNewQuoteIds}
          showSendQuoteButton={showSendQuoteButton}
          country={country}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          businessInfo={businessInfo!}
          disabled={false}
          additionalInsuredInfoListByAppId={additionalInsuredInfoListByAppId}
          validateAdditionalInsured={validateAdditionalInsured}
          versionData={versionData}
          isCustomerAgreementActive={isCustomerAgreementActive}
          isAuthenticated={isAuthenticated}
          isAuthProviderLoading={isAuthProviderLoading}
          paymentGraphqlClient={paymentGraphqlClient}
          transactionType={primaryQuoteResponse.transactionType}
          quoteExpireDate={primaryQuoteResponse.quoteExpireDate}
          refetchBusinessInfo={refetchBusinessInfo}
        />
      </div>
      <BackNavigationPopup
        displayModal={isBackBlockedNoticeOpen}
        handleClose={() => setIsBackBlockedNoticeOpen(false)}
      />
    </div>
  );
};

export default Quote;
